import React from "react";

import { FieldPath, FieldValues } from "react-hook-form";
import { ToggleButtonGroupProps } from "@mui/material";

import { IdNamePair } from "@/shared/domain";

import ControllerInput, { ControllerInputProps } from "../ControllerInput/ControllerInput";
import { StyledToggleButton, StyledToggleButtonGroup } from "./styled";

export type RadioButtonGroupProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerInputProps<TFieldValues, TName>, "render"> &
    ToggleButtonGroupProps & {
        options: IdNamePair[];
    };

const RadioButtonGroup = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    name,
    control,
    defaultValue = null,
    FormControlProps,
    LabelProps,
    hideErrorMessage,
    label,
    fullWidth,
    required = false,
    options,
    ...radioButtonGroupProps
}: RadioButtonGroupProps<TFieldValues, TName>): React.ReactElement => {
    return (
        <ControllerInput
            control={control}
            name={name}
            defaultValue={defaultValue}
            label={label}
            LabelProps={LabelProps}
            FormControlProps={FormControlProps}
            required={required}
            fullWidth={fullWidth}
            hideErrorMessage={hideErrorMessage}
            render={({ field: { onChange, value } }) => (
                <StyledToggleButtonGroup
                    value={value}
                    onChange={onChange}
                    fullWidth={fullWidth}
                    exclusive
                    {...radioButtonGroupProps}
                >
                    {options.map((option) => (
                        <StyledToggleButton key={option.id} value={option.id}>
                            {option.name}
                        </StyledToggleButton>
                    ))}
                </StyledToggleButtonGroup>
            )}
        />
    );
};

export default RadioButtonGroup;
